import { can, type Task } from '@selfon/domain/browser';
import { useEffect, useState } from 'react';
import { api, uploadToSignedUrl, type PlanDraft } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { shrinkImage, TASK_LABEL, thisMonthKst, time } from '../format';

const TONE = { ASSIGNED: 'muted', RUNNING: 'info', SUBMITTED: 'warn', DONE: 'ok', REDO: 'danger' } as const;
const mmss = (s: number) => `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, '0')}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

export function Tasks() {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const clients = useLoad(() => api.clients(centerId), [centerId]);
  const [clientId, setClientId] = useState<string | null>(null);
  useEffect(() => { if (!clientId && clients.data?.clients[0]) setClientId(clients.data.clients[0].id); }, [clients.data, clientId]);
  const client = clients.data?.clients.find((c) => c.id === clientId);
  const isGuardian = me!.role === 'GUARDIAN';

  return (
    <>
      <PageHeader title={isGuardian ? '오늘의 과제' : '치료 과제'}
        actions={clients.data && clients.data.clients.length > 1 && (
          <div className="inline-field">
            <label htmlFor="client">{isGuardian ? '자녀' : '이용자'}</label>
            <select id="client" value={clientId ?? ''} onChange={(e) => setClientId(e.target.value)}>
              {clients.data.clients.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
            </select>
          </div>
        )} />
      {clients.error && <Alert>{clients.error}</Alert>}
      {clients.loading && !clients.data && <Loading />}
      {clients.data?.clients.length === 0 && <Empty>{isGuardian ? '연결된 자녀가 없습니다. 센터에 문의하세요.' : '담당 이용자가 없습니다.'}</Empty>}
      {client && (isGuardian ? <GuardianTasks key={client.id} clientId={client.id} name={client.displayName} /> : <TherapistTasks key={client.id} clientId={client.id} name={client.displayName} goals={client.goals ?? []} />)}
    </>
  );
}

/** 점수 기준. 선생님마다 같은 뜻으로 매기도록 화면에 함께 보여 준다. */
const SCORE_GUIDE: Array<[number, string]> = [[5, '혼자 끝까지 해냈어요'], [4, '말로 조금 도와주면 해냈어요'], [3, '일부를 함께 해냈어요'], [2, '대부분 도움을 받았어요'], [1, '시도했어요']];

/** 걸린 시간. 1분 미만이나 기록이 없을 때 '0분'으로 보이지 않게 한다. */
export function durationText(ms?: number) {
  if (!ms) return '걸린 시간 기록 없음';
  return ms < 60000 ? '1분 미만 걸림' : `${Math.round(ms / 60000)}분 걸림`;
}

function TherapistTasks({ clientId, name, goals }: { clientId: string; name: string; goals: Array<{ id: string; text: string; term: 'SHORT' | 'LONG' }> }) {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const tasks = useLoad(() => api.tasks(centerId, clientId), [centerId, clientId]);
  const [lines, setLines] = useState('');
  const [runMin, setRunMin] = useState('5');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [evaluating, setEvaluating] = useState<Task | null>(null);
  const [ev, setEv] = useState({ decision: '' as '' | 'DONE' | 'REDO', score: '', comment: '' });
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const list = tasks.data?.tasks ?? [];
  const done = list.filter((t) => t.status === 'DONE').length;

  const assign = async () => {
    const titles = [...new Set(lines.split(/\r?\n/).map((l) => l.trim()).filter(Boolean))];
    if (!titles.length) { setMsg({ kind: 'error', text: '과제를 한 줄에 하나씩 입력하세요.' }); return; }
    try {
      await api.assignTasks(centerId, clientId, titles.slice(0, 20).map((title) => ({ title, runSec: Number(runMin) * 60 })));
      setLines(''); setMsg({ kind: 'ok', text: `${name}에게 과제 ${Math.min(20, titles.length)}개를 냈습니다.` }); tasks.reload();
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); }
  };

  const makeDraft = async () => {
    setDrafting(true); setMsg(null);
    try { setDraft(await api.planDraft(centerId, clientId, thisMonthKst())); } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setDrafting(false); }
  };

  return (
    <div className="grid-2">
      <section className="stack">
        {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
        <div className="card stack">
          <h2>과제 내기</h2>
          <Field id="lines" label="과제 (한 줄에 하나, 최대 20개)">
            <textarea id="lines" rows={4} value={lines} onChange={(e) => setLines(e.target.value)} placeholder={'책상 정리하기\n그림책 3쪽 소리 내어 읽기'} />
          </Field>
          <div className="row">
            <div className="inline-field">
              <label htmlFor="runMin">진행 시간</label>
              <select id="runMin" value={runMin} onChange={(e) => setRunMin(e.target.value)}>
                {['1', '3', '5', '10', '15', '20', '30'].map((m) => <option key={m} value={m}>{m}분</option>)}
              </select>
            </div>
            <button type="button" className="btn btn-primary" onClick={assign} disabled={!can(p, 'task:assign')}>과제 내기</button>
          </div>
        </div>
        {goals.length > 0 && (
          <div className="card stack">
            <h2>서비스계획 목표</h2>
            <ul className="plain">{goals.map((g) => <li key={g.id}><Chip tone="muted">{g.term === 'SHORT' ? '단기' : '장기'}</Chip> {g.text}</li>)}</ul>
            {can(p, 'ai:draft') && <button type="button" className="btn btn-outline" onClick={makeDraft} disabled={drafting}>{drafting ? '초안 만드는 중…' : 'AI로 이달 모니터링 초안 만들기'}</button>}
            {draft && (
              <div className="draft" aria-live="polite">
                <Alert kind="info">{draft.notice}</Alert>
                <h3>단기목표 달성</h3><p>{draft.draft.shortTermProgress}</p>
                <h3>장기목표 달성</h3><p>{draft.draft.longTermProgress}</p>
                <h3>향후 계획</h3><p>{draft.draft.nextPlan}</p>
                <p className="hint">개인정보는 지우고 보냈습니다. 저장되지 않았습니다.</p>
              </div>
            )}
          </div>
        )}
      </section>
      <section className="stack">
        <div className="progress-head">
          <h2>{name}의 과제</h2>
          <span className="muted">{done}/{list.length} 완료</span>
        </div>
        <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={list.length ? Math.round((done / list.length) * 100) : 0} aria-label="과제 완료율">
          <span style={{ width: `${list.length ? (done / list.length) * 100 : 0}%` }} />
        </div>
        {tasks.error && <Alert>{tasks.error}</Alert>}
        {tasks.loading && !tasks.data && <Loading />}
        {tasks.data && list.length === 0 && <Empty>아직 낸 과제가 없습니다. 왼쪽에서 과제를 내 보세요.</Empty>}
        <ul className="task-list">
          {list.slice().reverse().map((t) => (
            <li key={t.id} className="task">
              <div>
                <strong>{t.title}</strong>
                <p className="muted small">
                  {Math.round(t.runSec / 60)}분 과제{t.attempts > 1 ? ` · ${t.attempts}회차` : ''}
                  {t.submittedAt ? ` · ${time(t.submittedAt)} 제출` : ''}{t.submittedAt ? ` · ${durationText(t.durationMs)}` : ''}
                  {t.evaluation ? ` · ${'★'.repeat(t.evaluation.score)}` : ''}
                </p>
                {t.evaluation?.comment && <p className="small">“{t.evaluation.comment}”</p>}
              </div>
              <div className="task-right">
                <Chip tone={TONE[t.status]}>{TASK_LABEL[t.status]}</Chip>
                {['SUBMITTED', 'DONE', 'REDO'].includes(t.status) && t.photoKey && can(p, 'task:evaluate') && (
                  <button type="button" className={`btn btn-sm ${t.status === 'SUBMITTED' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => { setEv({ decision: t.evaluation?.decision ?? '', score: t.evaluation ? String(t.evaluation.score) : '', comment: t.evaluation?.comment ?? '' }); setEvaluating(t); }}>
                    {t.status === 'SUBMITTED' ? '평가하기' : '평가 수정'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <Dialog title="과제 평가" open={Boolean(evaluating)} onClose={() => setEvaluating(null)} submitLabel="평가 저장"
        onSubmit={async () => {
          if (!ev.decision) throw new Error('판정(완료 또는 다시 하기)을 골라 주세요.');
          if (!ev.score) throw new Error('수행 정도를 골라 주세요.');
          await api.evaluateTask(centerId, evaluating!.id, { expectedVersion: evaluating!.version, decision: ev.decision, score: Number(ev.score), comment: ev.comment || undefined }); tasks.reload(); }}>
        {evaluating && <p><strong>{evaluating.title}</strong> · {name}</p>}
        <fieldset className="radios">
          <legend>판정</legend>
          <label><input type="radio" name="decision" checked={ev.decision === 'DONE'} onChange={() => setEv({ ...ev, decision: 'DONE' })} /> 완료</label>
          <label><input type="radio" name="decision" checked={ev.decision === 'REDO'} onChange={() => setEv({ ...ev, decision: 'REDO' })} /> 다시 하기</label>
        </fieldset>
        <Field id="score" label="수행 정도">
          <select id="score" value={ev.score} onChange={(e) => setEv({ ...ev, score: e.target.value })}>
            <option value="">선택하세요</option>
            {SCORE_GUIDE.map(([n, text]) => <option key={n} value={n}>{'★'.repeat(n)} {n}점 · {text}</option>)}
          </select>
        </Field>
        <Field id="comment" label="아이에게 전할 말 (선택)"><textarea id="comment" rows={2} maxLength={300} value={ev.comment} onChange={(e) => setEv({ ...ev, comment: e.target.value })} /></Field>
      </Dialog>
    </div>
  );
}

/** 보호자·자녀 모드·학생 공용: 남은 과제 카드 → 작업장 → 사진 제출 */
export function GuardianTasks({ clientId, name }: { clientId: string; name: string }) {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const tasks = useLoad(() => api.tasks(centerId, clientId), [centerId, clientId]);
  const [active, setActive] = useState<Task | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const list = tasks.data?.tasks ?? [];
  const todo = list.filter((t) => ['ASSIGNED', 'REDO', 'RUNNING'].includes(t.status));
  const finished = list.filter((t) => ['SUBMITTED', 'DONE'].includes(t.status)).reverse();

  const start = async (t: Task) => {
    setMsg(null);
    try { setActive(t.status === 'RUNNING' ? t : (await api.startTask(centerId, t.id, t.version)).task); tasks.reload(); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); }
  };

  if (active) return <Workspace task={active} onDone={(text) => { setActive(null); setMsg({ kind: 'ok', text }); tasks.reload(); }} onExit={() => { setActive(null); tasks.reload(); }} />;

  return (
    <div className="stack">
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      {tasks.error && <Alert>{tasks.error}</Alert>}
      {tasks.loading && !tasks.data && <Loading />}
      {tasks.data && todo.length === 0 && <Empty>{name}에게 남은 과제가 없어요. 잘했어요!</Empty>}
      <ul className="task-cards">
        {todo.map((t) => (
          <li key={t.id} className={`task-card ${t.status === 'REDO' ? 'redo' : ''}`}>
            <div>
              <h3>{t.title}</h3>
              <p className="muted">{Math.round(t.runSec / 60)}분 동안 해요</p>
              {t.status === 'REDO' && <p className="small">선생님이 한 번 더 해 보자고 했어요{t.evaluation?.comment ? ` — “${t.evaluation.comment}”` : ''}</p>}
            </div>
            <button type="button" className="btn btn-primary btn-lg" onClick={() => start(t)}>{t.status === 'RUNNING' ? '이어서 하기' : '시작하기'}</button>
          </li>
        ))}
      </ul>
      {finished.length > 0 && (
        <div className="card">
          <h2>끝낸 과제</h2>
          <ul className="plain">
            {finished.map((t) => <li key={t.id}><Chip tone={TONE[t.status]}>{t.status === 'DONE' ? '완료' : '선생님 확인 중'}</Chip> {t.title}{t.evaluation && t.status === 'DONE' ? ` ${'★'.repeat(t.evaluation.score)}` : ''}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

/** 작업장: 남은 시간 표시 → 결과물 사진 선택(기기에서 축소) → S3 직접 업로드 → 제출 */
function Workspace({ task, onDone, onExit }: { task: Task; onDone: (msg: string) => void; onExit: () => void }) {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const startedAt = task.startedAt ? new Date(task.startedAt).getTime() : Date.now();
  const [left, setLeft] = useState(() => Math.ceil((startedAt + task.runSec * 1000 - Date.now()) / 1000));
  const [photo, setPhoto] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = window.setInterval(() => setLeft(Math.ceil((startedAt + task.runSec * 1000 - Date.now()) / 1000)), 500);
    return () => window.clearInterval(t);
  }, [startedAt, task.runSec]);
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.url); }, [photo]);

  const pick = async (file?: File) => {
    setError(null);
    if (!file) return;
    try { const blob = await shrinkImage(file); setPhoto({ blob, url: URL.createObjectURL(blob) }); } catch (e) { setError(errorText(e)); }
  };
  const submit = async () => {
    if (!photo) return;
    setBusy(true); setError(null);
    try {
      const up = await api.photoUrl(centerId, task.id, 'image/jpeg', photo.blob.size);
      await uploadToSignedUrl(up.url, up.headers, photo.blob);
      await api.submitTask(centerId, task.id, task.version, up.key);
      onDone(`'${task.title}' 결과물을 제출했어요. 선생님이 확인할 거예요.`);
    } catch (e) { setError(errorText(e)); setBusy(false); }
  };

  return (
    <section className="workspace" aria-labelledby="ws-title">
      <div className="ws-top"><span className="chip chip-info">작업장</span><button type="button" className="btn btn-ghost" onClick={onExit}>잠시 나가기</button></div>
      <h2 id="ws-title">{task.title}</h2>
      <div className={`timer ${left <= 0 ? 'over' : ''}`} role="timer" aria-live="off">{left > 0 ? mmss(left) : '시간 끝!'}</div>
      <p className="muted">{left > 0 ? '다 하면 결과물 사진을 올려요.' : '시간이 다 됐어요. 결과물 사진을 올려 주세요.'}</p>
      <label className="btn btn-outline btn-lg file-label" htmlFor="photo">📷 결과물 사진 고르기</label>
      <input id="photo" className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(e) => pick(e.target.files?.[0])} />
      {photo && <img className="preview" src={photo.url} alt={`${task.title} 결과물 미리보기`} />}
      {error && <Alert>{error}</Alert>}
      <button type="button" className="btn btn-primary btn-lg" onClick={submit} disabled={!photo || busy}>{busy ? '올리는 중…' : '제출하기'}</button>
    </section>
  );
}
